import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useContext,
  useState
} from "react";

import { useTranslation } from "react-i18next";

import { AxiosError } from "axios";
import { ERROR_CODES, USER_INFO_ERROR_KEYS, USER_INFO_LENGTHS } from "@/shared";

import useAxiosPrivate from "../../../api/hooks/useAxiosPrivate";
import useRefreshToken from "../../../api/hooks/useRefreshToken";
import { ApiResources } from "../../../api/resources";
import { AlertContext } from "../../../context/alert/AlertContextProvider";
import { ALERT_STATUS } from "../../../context/alert/types";
import { useAuth } from "../../../context/Auth/AuthProvider";
import { sendCodeToEmail } from "../../../shared/functions/functions";
import useResponse from "../../../shared/hooks/useResponse";
import { AUTHENTICATION_TYPES, ENABLE_AUTHENTICATION_STEPS } from "../types";
import { useValidateOtp } from "./useValidateOtp";

const useEnableTwoFactorAuthentication = () => {
  const { setAlert } = useContext(AlertContext);

  const { toggleTwoFaEnable, setTwoFaMethod } = useAuth();
  const refreshToken = useRefreshToken();

  const { postData } = useAxiosPrivate();
  const { handleResponse, handleCommonError } = useResponse();
  const { t } = useTranslation();

  const [step, setStep] = useState(ENABLE_AUTHENTICATION_STEPS.SelectMethod);
  const [isLoading, setIsLoading] = useState(false);
  const [authType, setAuthType] = useState<AUTHENTICATION_TYPES | null>(null);
  const [secretKey, setSecretKey] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const { error, setError, validateOtp } = useValidateOtp();

  const [payload, setPayload] = useState<Object | null>(null);

  const isSuccessMessageStep =
    step === ENABLE_AUTHENTICATION_STEPS.SuccessMessage;

  const handleClose = async () => {
    if (isSuccessMessageStep) {
      // update token before calling the rest of the api's
      await refreshToken().then(() => {
        toggleTwoFaEnable(true); // this line actually closes the 2FA requirement page

        setAlert(
          ALERT_STATUS.Success,
          t("Password##two-factor authentication enabled")
        );
      });
    }
  };

  const changeAuthenticationType = (newType: AUTHENTICATION_TYPES | null) => {
    setTwoFaMethod(newType);
    setAuthType(newType);
  };

  const changeStep = (newStep: ENABLE_AUTHENTICATION_STEPS) => {
    return setStep(newStep);
  };

  const changeOtpCode = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = event.target.value;
    const trimmedValue = newValue.trim();

    if (!trimmedValue) {
      setError(null);
    }

    validateOtp(event);

    if (trimmedValue.length <= USER_INFO_LENGTHS.OtpMax) {
      setOtpCode(trimmedValue);
      setPayload({
        otpCode: trimmedValue,
        Method: authType
      });
    }
  };

  const getSecretKeySubmit = async () => {
    try {
      setIsLoading(true);

      await postData(ApiResources.GenerateTwoFactorKey).then(response => {
        refreshToken(); // update token, otherwise firt enableTwofa api call will fail - related to the issues in BE
        setSecretKey(response?.data.key);
        changeStep(ENABLE_AUTHENTICATION_STEPS.SendOtpCode);
      });
    } catch (error) {
      setSecretKey(""); // reset secret key
      const err = error as AxiosError<any>;

      if (err) {
        const responseData: string = err.response?.data
          ? t(`Alert##${err.response?.data.message}`)
          : t("Alert##action failed");
        handleResponse(ALERT_STATUS.Critical, responseData);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFaError = (
    error: AxiosError,
    setErr?: Dispatch<SetStateAction<string | null>>
  ) => {
    const errorString = USER_INFO_ERROR_KEYS.IncorrectCode;

    if (error.response?.status === ERROR_CODES.BadRequest) {
      setErr ? setErr(errorString) : setError(errorString);
    } else {
      handleResponse(ALERT_STATUS.Critical, error as any);
    }
  };

  const getEmailCodeSubmit = () => {
    const successCallback = () =>
      changeStep(ENABLE_AUTHENTICATION_STEPS.SendEmailCode);

    sendCodeToEmail(
      setIsLoading,
      successCallback,
      postData,
      handleCommonError,
      t
    );
  };

  const sendOtpCodeSubmit = async () => {
    try {
      setIsLoading(true);

      await postData(ApiResources.EnableTwofactor, payload);

      changeStep(ENABLE_AUTHENTICATION_STEPS.SuccessMessage);
    } catch (error) {
      handleTwoFaError(error as AxiosError<any>);
    }

    setIsLoading(false);
  };

  return {
    authType,
    error,
    isLoading,
    otpCode,
    secretKey,
    step,
    changeAuthenticationType,
    changeOtpCode,
    changeStep,
    getEmailCodeSubmit,
    getSecretKeySubmit,
    handleClose,
    sendOtpCodeSubmit,
    setError,
    setOtpCode,
    setSecretKey,
    validateOtp
  };
};

export { useEnableTwoFactorAuthentication };
